import { IProductTypeSize } from '@cakeiteasy/web-utils';

import { ICakeCartLine } from '../../cart/models/cart/i-cake-cart-line';
import { CART_LINE_TYPES } from '../../cart/constants/common';

export const sizeHasMultipleVariants = (size: IProductTypeSize) => {
  const { questions, printingPictures, extraGroups, deposits } = size;
  return (
    questions.length > 0 ||
    printingPictures.length > 0 ||
    extraGroups.length > 0 ||
    deposits.length > 0
  );
};

export const typeHasMultipleVariants = (sizes: IProductTypeSize[]) => {
  const [size] = sizes;
  const areMultipleSizes = sizes.length > 1 || size.products.length > 1;
  const areMultipleVariants = sizeHasMultipleVariants(size);

  return areMultipleSizes || areMultipleVariants;
};

export const getCartLineByTypeId = (productTypeId: number, cartLines: ICakeCartLine[]) =>
  cartLines.find(line => !(line.details && line.details.applyOnce) && line.product.type.id === productTypeId);

export const getCartLinesByTypeId = (productTypeId: number, cartLines: ICakeCartLine[]) =>
  cartLines.filter(line => !(line.details && line.details.applyOnce) && line.product.type.id === productTypeId);

/** Number of separately built versions among the lines: one per orderLineBuilderId, lines without one count once. */
export const getCartBuildsCount = (cartLines: ICakeCartLine[]): number =>
  new Set((cartLines || []).map(line => line.orderLineBuilderId ?? null)).size;

export const getCartLinesByParentTypeId =(productTypeId: number, cartLines: ICakeCartLine[]) => {
  return cartLines.filter(line => line.parentType === productTypeId);
};

const groupLinesByType = (orderLines: ICakeCartLine[]) => {
  return orderLines.reduce<Record<string, ICakeCartLine[]>>((acc, oL) => {
    const { type } = oL;
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(oL);
    return acc;
  }, {});
};

const processAddonType = (type: string, lines: ICakeCartLine[]): string => {
  const nameTypes = new Set([
    CART_LINE_TYPES.QUESTION,
    CART_LINE_TYPES.PICTURE,
    CART_LINE_TYPES.EXTRA_PRODUCT,
  ]);

  const singleItemTypes = new Set([
    CART_LINE_TYPES.DEPOSIT,
    CART_LINE_TYPES.GREETING_CARD,
  ]);

  if (nameTypes.has(type)) {
    return lines.map(line => line.name).join(', ');
  }

  if (singleItemTypes.has(type)) {
    const [line] = lines;
    return line.name || '';
  }

  return '';
};

export const getAddonsString = (cartLine: ICakeCartLine): string => {
  const { orderLines = [] } = cartLine;

  const linesByType = groupLinesByType(orderLines);
  const addonStrings = Object.keys(linesByType)
    .map((key) => processAddonType(key, linesByType[key]));

  return addonStrings.length > 0 ? `${addonStrings.join(', ')}.` : '';
};

export const getChosenMenu = (cartLines: ICakeCartLine[]) => {
  return (cartLines || [])
    .reduce<Record<number, { count: number, variantName: string, productTypeName: string }[]>>(
    (acc, line) => {
      const {
        count,
        product: { type: { id: productTypeId, name } },
        variant: { name: variantName },
      } = line;

      if (!acc[productTypeId]) {
        acc[productTypeId] = [];
      }
      acc[productTypeId].push({
        count,
        variantName,
        productTypeName: name,
      });
      return acc;
      },
      {}
    );
};

const MODAL_CLICK_IGNORE_SELECTOR = [
  'button',
  'a',
  'input',
  'select',
  'textarea',
  '[data-modal-ignore]',
].join(', ');

export const isModalClickIgnored = (event: MouseEvent): boolean => {
  const target = event.target instanceof Element ? event.target : null;

  return !!(target && target.closest(MODAL_CLICK_IGNORE_SELECTOR));
};
